import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useBusinessNoticeSelectList,
  getBusinessNoticeAlbumList,
  sendBusinessNotice,
} from "~/services/business/notify";
import {
  streamDiaryBatch,
  type DiaryBatchItem,
} from "~/services/business/diary";
import { checkActionCost } from "~/services/business/credit";
import type {
  BusinessNoticeSelectListResponse,
  BusinessNoticeSendRequest,
} from "~/types/common";

type SendStatus = "idle" | "sending" | "sent" | "failed";

interface PetRow {
  petSeq: number;
  petName: string;
  fileUrl?: string | null;
}

interface DraftResult {
  body: string;
  ok: boolean;
}

/** 선택된 미디어 1건 */
interface PickedMedia {
  seq: number;
  url: string;
  thumb: string;
  type: "image" | "video";
}

const MAX_IMAGES = 8;

const isVideoUrl = (url: string) =>
  /\.(mp4|mov|avi|webm|mkv|m4v|3gp)(\?|$)/i.test(url);

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(
    d.getDate(),
  ).padStart(2, "0")}`;
};
const todayInput = () => {
  const t = todayYmd();
  return `${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 8)}`;
};
/**
 * datetime-local 기본값 — 지금부터 +30분을 10분 단위로 올림 (YYYY-MM-DDTHH:mm).
 * 예약 발송 배치는 10분마다 돌며 분 단위로 예약 시각을 비교하므로 10분 격자에 맞춘다.
 */
const nowPlus30Local = () => {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(Math.ceil((d.getMinutes() + 30) / 10) * 10);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
};

/**
 * 사진·영상 첨부 팝업 — 날짜를 골라 그 날짜에 업로드된 사진/영상을 선택.
 * 확인 시 선택 목록을 부모에 반환.
 */
function MediaPickerModal({
  pet,
  initial,
  onConfirm,
  onClose,
}: {
  pet: PetRow;
  initial: PickedMedia[];
  onConfirm: (picked: PickedMedia[]) => void;
  onClose: () => void;
}) {
  const [dateInput, setDateInput] = useState(todayInput());
  const date = dateInput.replace(/-/g, "");
  const [sel, setSel] = useState<Set<number>>(
    new Set(initial.map((m) => m.seq)),
  );

  const { data, isLoading } = useQuery({
    queryKey: ["batch-album", pet.petSeq, date],
    queryFn: () =>
      getBusinessNoticeAlbumList({
        date,
        recipients: [
          {
            petSeq: pet.petSeq,
            userSeq: pet.petSeq,
            petName: pet.petName,
            selected: true,
          },
        ],
      }),
    staleTime: 60_000,
  });

  const media: PickedMedia[] = useMemo(
    () =>
      (data?.data ?? [])
        .filter((it) => it.fileSeq && it.fileUrl)
        .map((it) => ({
          seq: it.fileSeq as number,
          url: it.fileUrl || "",
          thumb: it.thumbnailUrl || it.fileUrl || "",
          type: isVideoUrl(it.fileUrl || "") ? "video" : "image",
        })),
    [data],
  );

  const toggle = (seq: number) =>
    setSel((prev) => {
      const next = new Set(prev);
      if (next.has(seq)) next.delete(seq);
      else next.add(seq);
      return next;
    });

  const confirm = () => {
    // 이번 날짜 선택 + 다른 날짜에서 이미 고른 것 유지(초기 목록 중 이 날짜에 없는 seq)
    const thisDate = media.filter((m) => sel.has(m.seq));
    const otherDates = initial.filter(
      (m) => !media.some((x) => x.seq === m.seq),
    );
    onConfirm([...otherDates, ...thisDate]);
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end bg-black/40">
      <div className="max-h-[80vh] rounded-t-2xl bg-white p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-[15px] font-medium">
            {pet.petName} 사진·영상 첨부
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-[20px] leading-none text-[#888780]"
          >
            ×
          </button>
        </div>

        <div className="mb-3 flex items-center gap-2">
          <span className="text-[13px] text-[#5F5E5A]">업로드 날짜</span>
          <input
            type="date"
            value={dateInput}
            onChange={(e) => setDateInput(e.target.value)}
            className="ml-auto rounded border border-[#E9E9E9] p-1.5 text-[13px]"
          />
        </div>

        <div className="min-h-[160px] max-h-[46vh] overflow-y-auto">
          {isLoading ? (
            <div className="py-10 text-center text-[13px] text-[#B7B5AC]">
              불러오는 중…
            </div>
          ) : media.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-[#888780]">
              이 날짜에 업로드된 사진·영상이 없어요.
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {media.map((m) => {
                const on = sel.has(m.seq);
                return (
                  <button
                    key={m.seq}
                    type="button"
                    onClick={() => toggle(m.seq)}
                    className={`relative aspect-square overflow-hidden rounded-lg border-2 ${
                      on ? "border-[#FF9934]" : "border-transparent"
                    }`}
                  >
                    <img
                      src={m.thumb}
                      alt=""
                      className={`h-full w-full object-cover ${on ? "" : "opacity-70"}`}
                    />
                    {m.type === "video" && (
                      <span className="absolute bottom-1 right-1 rounded bg-black/50 px-1 text-[10px] text-white">
                        ▶
                      </span>
                    )}
                    {on && (
                      <span className="absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF9934] text-[11px] leading-none text-white">
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-[#E9E9E9] py-3 text-[14px] text-[#5F5E5A]"
          >
            취소
          </button>
          <button
            type="button"
            onClick={confirm}
            className="flex-1 rounded-lg bg-[#FF9934] py-3 text-[14px] font-medium text-white"
          >
            선택 완료 ({sel.size})
          </button>
        </div>
      </div>
    </div>
  );
}

/** 펫 행 — 접힘=겹친 썸네일 칩 + 초안 1줄, 탭하면 인라인 확장(첨부·메모·초안). */
function BatchPetRow({
  pet,
  running,
  selected,
  expanded,
  memo,
  picked,
  result,
  sendAt,
  sendStatus,
  onToggleSelect,
  onToggleExpand,
  onMemoChange,
  onOpenPicker,
  onResultChange,
  onResetDraft,
  onSendAtChange,
  onSend,
}: {
  pet: PetRow;
  running: boolean;
  selected: boolean;
  expanded: boolean;
  memo: string;
  picked: PickedMedia[];
  result?: DraftResult;
  sendAt: string;
  sendStatus: SendStatus;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
  onMemoChange: (v: string) => void;
  onOpenPicker: () => void;
  onResultChange: (body: string) => void;
  onResetDraft: () => void;
  onSendAtChange: (v: string) => void;
  onSend: () => void;
}) {
  const imgN = picked.filter((m) => m.type === "image").length;
  const vidN = picked.filter((m) => m.type === "video").length;

  return (
    <div
      className={`rounded-lg border ${
        expanded ? "border-[#FFD79A] bg-[#FFFCF6]" : "border-[#F1EFE8]"
      }`}
    >
      <div className="flex items-center gap-2 p-2">
        <button
          type="button"
          disabled={running}
          onClick={onToggleSelect}
          className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border text-[11px] ${
            selected
              ? "border-[#FF9934] bg-[#FF9934] text-white"
              : "border-[#D3D1C7] text-transparent"
          }`}
        >
          ✓
        </button>
        <button
          type="button"
          onClick={onToggleExpand}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <span className="flex-shrink-0 whitespace-nowrap text-[14px]">
            {pet.petName}
          </span>
          {picked.length > 0 && (
            <span className="flex flex-shrink-0 items-center">
              {picked.slice(0, 3).map((m, i) => (
                <span
                  key={m.seq}
                  className="relative h-6 w-6 overflow-hidden rounded-md border-[1.5px] border-white bg-[#F1EFE8]"
                  style={{ marginLeft: i ? -9 : 0, zIndex: 3 - i }}
                >
                  <img
                    src={m.thumb}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                  {m.type === "video" && (
                    <span className="absolute inset-0 flex items-center justify-center text-[8px] text-white">
                      ▶
                    </span>
                  )}
                </span>
              ))}
              <span className="ml-1.5 text-[11px] text-[#888780]">
                {imgN > 0 && imgN}
                {imgN > 0 && vidN > 0 && "·"}
                {vidN > 0 && `▶${vidN}`}
              </span>
            </span>
          )}
          <span className="min-w-0 flex-1" />
          {(sendStatus === "sent" || sendStatus === "failed" || result) && (
            <span
              className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                sendStatus === "sent"
                  ? "bg-[#E8F6EF] text-[#1D9E75]"
                  : sendStatus === "failed"
                    ? "bg-[#FCE9F0] text-[#D4537E]"
                    : result?.ok
                      ? "bg-[#E8F6EF] text-[#1D9E75]"
                      : "bg-[#FCE9F0] text-[#D4537E]"
              }`}
            >
              {sendStatus === "sent"
                ? "발송됨"
                : sendStatus === "failed"
                  ? "발송실패"
                  : result?.ok
                    ? "완료"
                    : "실패"}
            </span>
          )}
          <span className="flex-shrink-0 text-[11px] text-[#C9C7BE]">
            {expanded ? "▲" : "▼"}
          </span>
        </button>
      </div>

      {expanded && (
        <div className="flex flex-col gap-2 px-3 pb-3">
          <div className="flex items-center gap-2">
            {picked.slice(0, 6).map((m) => (
              <span
                key={m.seq}
                className="relative h-9 w-9 overflow-hidden rounded-md border border-[#F1EFE8]"
              >
                <img
                  src={m.thumb}
                  alt=""
                  className="h-full w-full object-cover"
                />
                {m.type === "video" && (
                  <span className="absolute bottom-0 right-0 bg-black/50 px-0.5 text-[8px] text-white">
                    ▶
                  </span>
                )}
              </span>
            ))}
            <button
              type="button"
              disabled={running}
              onClick={onOpenPicker}
              aria-label={
                picked.length > 0 ? "사진·영상 변경" : "사진·영상 첨부"
              }
              className="flex h-9 w-9 items-center justify-center rounded-md border border-[#FFD79A] bg-white text-[#BA7517] disabled:opacity-40"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
                <circle cx="12" cy="13" r="3.2" />
              </svg>
            </button>
          </div>
          {/* 초안 전: 메모 입력 / 초안 후: 메모는 숨기고 초안만 노출 */}
          {!result?.ok ? (
            <input
              className="w-full rounded border border-[#E9E9E9] p-2 text-[13px] disabled:bg-[#F7F6F2]"
              placeholder="메모 (예: 발톱 손질, 오후 낮잠)"
              value={memo}
              disabled={running}
              onChange={(e) => onMemoChange(e.target.value)}
            />
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#5F5E5A]">
                  초안
                </span>
                <button
                  type="button"
                  disabled={running || sendStatus === "sent"}
                  onClick={onResetDraft}
                  className="text-[11px] text-[#B89A6B] underline disabled:opacity-40"
                >
                  ↺ 초안 지우고 다시 쓰기
                </button>
              </div>
              <textarea
                className="w-full resize-none rounded-lg border border-[#F1EFE8] p-2 text-[13px] leading-relaxed"
                rows={6}
                value={result.body}
                onChange={(e) => onResultChange(e.target.value)}
              />
            </>
          )}

          {/* 발송 — 초안 완료분만 노출 */}
          {result?.ok && (
            <div className="flex flex-col gap-2 rounded-lg border border-[#F1EFE8] bg-[#FAFAF7] p-2">
              <label className="flex items-center gap-2 text-[12px] text-[#5F5E5A]">
                <input
                  type="checkbox"
                  checked={!!sendAt}
                  disabled={sendStatus === "sending" || sendStatus === "sent"}
                  onChange={(e) =>
                    onSendAtChange(e.target.checked ? nowPlus30Local() : "")
                  }
                />
                예약 발송
                {sendAt && (
                  <input
                    type="datetime-local"
                    step={600}
                    min={nowPlus30Local()}
                    value={sendAt}
                    disabled={sendStatus === "sending" || sendStatus === "sent"}
                    onChange={(e) => onSendAtChange(e.target.value)}
                    className="ml-auto rounded border border-[#E9E9E9] p-1 text-[12px]"
                  />
                )}
              </label>
              <button
                type="button"
                disabled={sendStatus === "sending" || sendStatus === "sent"}
                onClick={onSend}
                className={`w-full rounded-lg py-2.5 text-[13px] font-medium disabled:opacity-60 ${
                  sendStatus === "sent"
                    ? "bg-[#E8F6EF] text-[#1D9E75]"
                    : "bg-[#3D3B36] text-white"
                }`}
              >
                {sendStatus === "sent"
                  ? "발송 완료 ✓"
                  : sendStatus === "sending"
                    ? "발송 중…"
                    : sendAt
                      ? "예약 발송"
                      : "즉시 발송"}
              </button>
              {sendStatus === "failed" && (
                <span className="text-[11px] text-[#D4537E]">
                  발송에 실패했어요. 다시 시도해주세요.
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * AI 알림장 — 일괄 초안 생성 (아코디언 · 펫별 첨부 팝업 · 진행바 · 절전 방지).
 */
export function NotifyBatchPanel() {
  const { data } = useBusinessNoticeSelectList();
  const selectList = data as BusinessNoticeSelectListResponse | undefined;
  const pets: PetRow[] = selectList?.recipients ?? [];

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<number | null>(null);
  const [memos, setMemos] = useState<Record<number, string>>({});
  const [petMedia, setPetMedia] = useState<Record<number, PickedMedia[]>>({});
  const [results, setResults] = useState<Record<number, DraftResult>>({});
  const [pickerPet, setPickerPet] = useState<PetRow | null>(null);
  // 발송 — 펫별 예약시각("" = 즉시)과 발송 상태
  const [sendAt, setSendAt] = useState<Record<number, string>>({});
  const [sendStatus, setSendStatus] = useState<Record<number, SendStatus>>({});
  // done=완료, started=처리 시작(미디어 추출 포함 진행 중), total=전체
  const [progress, setProgress] = useState<{
    done: number;
    started: number;
    total: number;
  }>({
    done: 0,
    started: 0,
    total: 0,
  });
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── 진행 상태 표현 강화 (0%에 멈춘 것처럼 보이는 오해 방지) ──
  const PER_PET_SEC = 18; // 펫당 예상 소요(미디어 준비 + 생성 실측 기반)
  const BATCH_CONCURRENCY = 4; // 백엔드 동시 처리 마리 수와 맞춤(예상시간 계산용)
  const [nowTick, setNowTick] = useState(0); // 경과시간 갱신용 틱(0.5s)
  const startedAtRef = useRef<number>(0); // 배치 시작 시각
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNowTick(Date.now()), 500);
    return () => clearInterval(id);
  }, [running]);

  const textCostQ = useQuery({
    queryKey: ["ai-diary-cost", "USE_AI_DIARY_TEXT"],
    queryFn: () => checkActionCost("USE_AI_DIARY_TEXT"),
    staleTime: 5 * 60_000,
  });
  const imageCostQ = useQuery({
    queryKey: ["ai-diary-cost", "USE_AI_DIARY_IMAGE"],
    queryFn: () => checkActionCost("USE_AI_DIARY_IMAGE"),
    staleTime: 5 * 60_000,
  });
  const textPrice = textCostQ.data?.totalCost ?? 2;
  const imagePrice = imageCostQ.data?.totalCost ?? 30;

  // ── 화면 절전 방지 ─────────────────────────────
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const acquireWakeLock = async () => {
    try {
      if ("wakeLock" in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch {
      /* noop */
    }
  };
  const releaseWakeLock = async () => {
    try {
      await wakeLockRef.current?.release();
    } catch {
      /* noop */
    }
    wakeLockRef.current = null;
  };
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && running) acquireWakeLock();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [running]);
  useEffect(() => () => void releaseWakeLock(), []);

  const toggleSelect = (petSeq: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(petSeq)) next.delete(petSeq);
      else next.add(petSeq);
      return next;
    });

  const petCost = (petSeq: number) => {
    const hasMedia = (petMedia[petSeq]?.length ?? 0) > 0;
    const hasMemo = !!memos[petSeq]?.trim();
    return (hasMedia ? imagePrice : 0) + (hasMemo ? textPrice : 0);
  };
  const totalCost = [...selected].reduce((s, p) => s + petCost(p), 0);

  const run = async () => {
    const items: DiaryBatchItem[] = [...selected]
      .map((petSeq) => {
        const m = petMedia[petSeq] ?? [];
        const imageFileSeqs = m
          .filter((x) => x.type === "image")
          .slice(0, MAX_IMAGES)
          .map((x) => x.seq);
        const videoFileSeqs = m
          .filter((x) => x.type === "video")
          .map((x) => x.seq);
        return {
          petSeq,
          memo: memos[petSeq]?.trim() ? memos[petSeq].trim() : undefined,
          imageFileSeqs: imageFileSeqs.length ? imageFileSeqs : undefined,
          videoFileSeqs: videoFileSeqs.length ? videoFileSeqs : undefined,
        };
      })
      .filter((it) => it.memo || it.imageFileSeqs || it.videoFileSeqs);
    if (items.length === 0) {
      setError("메모를 적거나 사진·영상을 첨부한 펫을 선택해주세요.");
      return;
    }
    setRunning(true);
    setError(null);
    setResults({});
    setProgress({ done: 0, started: 0, total: items.length });
    startedAtRef.current = Date.now();
    setNowTick(Date.now());
    await acquireWakeLock();
    try {
      await streamDiaryBatch(items, {
        onStart: (total) => setProgress({ done: 0, started: 0, total }),
        onItemStart: () =>
          setProgress((p) => ({ ...p, started: p.started + 1 })),
        onDiary: (ev) => {
          setResults((prev) => ({
            ...prev,
            [ev.petSeq]: {
              body: ev.data?.body ?? "",
              ok: ev.code === "COMPLETE" && !!ev.data?.body,
            },
          }));
          setProgress((p) => ({ ...p, done: p.done + 1 }));
        },
        onError: (msg) => setError(msg),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "일괄 생성 실패");
    } finally {
      setRunning(false);
      await releaseWakeLock();
    }
  };

  // ── 발송 ───────────────────────────────────────
  // 단일 화면(useNotifySendInform)과 동일한 payload 로 펫별 발송.
  const buildSendRequest = (
    petSeq: number,
    body: string,
  ): BusinessNoticeSendRequest | null => {
    if (!selectList) return null;
    const at = sendAt[petSeq]?.trim();
    const dtReservation = at ? `${at}:00` : null; // datetime-local → +초
    const media = petMedia[petSeq] ?? [];
    return {
      bizUserSeq: selectList.bizUserSeq ?? 0,
      companySeq: selectList.companySeq ?? 0,
      noticeSeq: selectList.noticeSeq ?? null,
      notiTitle: null,
      notiContents: body,
      dtReservation,
      cdSender: selectList.cdSender || "SENDER_OWNER",
      cdRecipient: selectList.cdRecipient || "RECIPIENT_PET_OWNER",
      // 즉시 발송: tempSaveYn="N", flag=false / 예약: tempSaveYn="Y", flag=true
      reservationSendFlag: !!dtReservation,
      activityFlag: false,
      tempSaveYn: dtReservation ? "Y" : "N",
      schedules: selectList.schedules ?? [],
      noticeTypes: (selectList.noticeTypes ?? []).map((n) => ({
        ...n,
        selected: n.cdNotiType === "NOTI_DIARY",
      })),
      recipients: (selectList.recipients ?? []).map((r) => ({
        ...r,
        selected: r.petSeq === petSeq,
      })),
      activities: selectList.activities ?? [],
      photos: media.map((m) => ({
        notiPhotoSeq: null,
        fileSeq: m.seq,
        fileUrl: m.url,
      })),
    };
  };

  const sendOne = async (petSeq: number) => {
    const r = results[petSeq];
    if (!r?.ok) return;
    const req = buildSendRequest(petSeq, r.body);
    if (!req) return;
    setSendStatus((prev) => ({ ...prev, [petSeq]: "sending" }));
    try {
      const res = await sendBusinessNotice(req);
      setSendStatus((prev) => ({
        ...prev,
        [petSeq]: res?.statusCode === 200 ? "sent" : "failed",
      }));
    } catch {
      setSendStatus((prev) => ({ ...prev, [petSeq]: "failed" }));
    }
  };

  const sendAll = async () => {
    const targets = [...selected].filter(
      (p) => results[p]?.ok && sendStatus[p] !== "sent",
    );
    for (const p of targets) {
      // eslint-disable-next-line no-await-in-loop
      await sendOne(p);
    }
  };

  // 초안 초기화 — 결과·발송상태를 지워 메모 입력을 다시 활성화(펫 단위 / 전체)
  const resetDraft = (petSeq: number) => {
    setResults((prev) => {
      const next = { ...prev };
      delete next[petSeq];
      return next;
    });
    setSendStatus((prev) => {
      const next = { ...prev };
      delete next[petSeq];
      return next;
    });
  };
  const resetAllDrafts = () => {
    setResults({});
    setSendStatus({});
    setProgress({ done: 0, started: 0, total: 0 });
  };
  const hasAnyDraft = Object.keys(results).length > 0;

  const sendableCount = [...selected].filter(
    (p) => results[p]?.ok && sendStatus[p] !== "sent",
  ).length;
  const sentCount = [...selected].filter(
    (p) => sendStatus[p] === "sent",
  ).length;
  const sendingAny = Object.values(sendStatus).some((s) => s === "sending");

  const pct =
    progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  // ── 진행 표현: 처리 중(미디어 추출 포함)인 마리도 진척에 반영해 0%에 멈춰 보이지 않게 한다.
  //    완료=1.0, 처리중=0.5 로 가중 → 첫 완료 전에도 시작 즉시 바가 움직인다.
  const inProgress = Math.max(0, progress.started - progress.done);
  const weightedDone = progress.done + inProgress * 0.5;
  const displayPct =
    progress.total > 0
      ? running
        ? Math.min(99, Math.round((weightedDone / progress.total) * 100))
        : pct
      : 0;
  const elapsedSec = startedAtRef.current
    ? Math.max(0, Math.floor((nowTick - startedAtRef.current) / 1000))
    : 0;
  // 백엔드가 펫을 동시 처리(BATCH_CONCURRENCY)하므로 예상시간도 동시성만큼 나눈다.
  const estTotalSec =
    Math.ceil(progress.total / BATCH_CONCURRENCY) * PER_PET_SEC;
  const estMinLabel =
    estTotalSec >= 60
      ? `약 ${Math.ceil(estTotalSec / 60)}분`
      : `약 ${estTotalSec}초`;
  const fmtMMSS = (s: number) =>
    `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

  return (
    <div className="flex flex-col gap-4 pb-24">
      <div className="rounded-xl border-2 border-[#FFE0B3] bg-[#FFF7E6] p-3 text-[13px] leading-relaxed text-[#BA7517]">
        펫을 탭해 펼치고 사진·영상을 첨부(선택)하거나 메모만 남기면, 내 스타일로
        초안을 만들어드려요.
        <span className="mt-1 block text-[11px] text-[#9A7B3A]">
          생성 동안 화면이 꺼지지 않게 유지돼요. 완료까지 이 화면을 켜두세요.
        </span>
      </div>

      {(running || progress.total > 0) && (
        <div className="rounded-xl border-2 border-[#E9E9E9] bg-white p-3">
          <div className="mb-1 flex items-center justify-between text-[13px]">
            <span className="font-medium text-[#3D3B36]">
              {running ? "초안 생성 중…" : "생성 완료"}
            </span>
            <span className="text-[#888780]">
              {progress.done}/{progress.total} · {displayPct}%
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-[#F1EFE8]">
            <div
              className="h-full rounded-full bg-[#FF9934] transition-all duration-500 ease-linear"
              style={{ width: `${displayPct}%` }}
            />
          </div>
          {running && (
            <>
              <div className="mt-2 flex items-center justify-between text-[11px] text-[#9A7B3A]">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-1.5 w-1.5 animate-ping rounded-full bg-[#FF9934]" />
                  {inProgress > 0
                    ? `${inProgress}마리 처리 중(사진·영상 분석 포함)…`
                    : "마무리 중…"}
                </span>
                <span>
                  경과 {fmtMMSS(elapsedSec)} · 예상 {estMinLabel}
                </span>
              </div>
              <div className="mt-1 text-[11px] leading-relaxed text-[#B8A88A]">
                동영상은 장면을 분석하는 데 시간이 좀 걸려요. 멈춘 게 아니라
                처리 중이니 이 화면을 닫지 말고 기다려 주세요.
              </div>
            </>
          )}
        </div>
      )}

      <div className="rounded-xl border-2 border-[#E9E9E9] bg-white p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[14px] font-medium">
            펫 선택 {selected.size > 0 && `(${selected.size})`}
          </span>
          {hasAnyDraft && !running && (
            <button
              type="button"
              onClick={resetAllDrafts}
              className="text-[12px] text-[#B89A6B] underline"
            >
              ↺ 초안 전체 초기화
            </button>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {pets.map((p) => (
            <BatchPetRow
              key={p.petSeq}
              pet={p}
              running={running}
              selected={selected.has(p.petSeq)}
              expanded={expanded === p.petSeq}
              memo={memos[p.petSeq] ?? ""}
              picked={petMedia[p.petSeq] ?? []}
              result={results[p.petSeq]}
              sendAt={sendAt[p.petSeq] ?? ""}
              sendStatus={sendStatus[p.petSeq] ?? "idle"}
              onToggleSelect={() => toggleSelect(p.petSeq)}
              onToggleExpand={() =>
                setExpanded((cur) => (cur === p.petSeq ? null : p.petSeq))
              }
              onMemoChange={(v) =>
                setMemos((prev) => ({ ...prev, [p.petSeq]: v }))
              }
              onOpenPicker={() => setPickerPet(p)}
              onResultChange={(body) =>
                setResults((prev) => ({
                  ...prev,
                  [p.petSeq]: { ...prev[p.petSeq], body },
                }))
              }
              onResetDraft={() => resetDraft(p.petSeq)}
              onSendAtChange={(v) =>
                setSendAt((prev) => ({ ...prev, [p.petSeq]: v }))
              }
              onSend={() => sendOne(p.petSeq)}
            />
          ))}
          {pets.length === 0 && (
            <div className="py-6 text-center text-[13px] text-[#888780]">
              보낼 수 있는 펫이 없어요.
            </div>
          )}
        </div>

        {selected.size > 0 && totalCost > 0 && !running && (
          <div className="mt-2 text-[12px] text-[#888780]">
            생성 성공분에 대해 총{" "}
            <b className="text-[#BA7517]">{totalCost}크레딧</b> 차감돼요.
          </div>
        )}

        <button
          type="button"
          disabled={running || selected.size === 0}
          className="mt-3 w-full rounded-lg bg-[#FF9934] py-3 text-[14px] font-medium text-white disabled:opacity-40"
          onClick={run}
        >
          {running
            ? `초안 생성 중… (${progress.done}/${progress.total})`
            : `일괄 초안 생성 (${selected.size})`}
        </button>
        {error && (
          <div className="mt-2 text-[12px] text-[#D4537E]">{error}</div>
        )}

        {/* 전체 발송 — 초안 완료분 일괄 발송 */}
        {sendableCount > 0 && (
          <button
            type="button"
            disabled={sendingAny}
            className="mt-2 w-full rounded-lg bg-[#3D3B36] py-3 text-[14px] font-medium text-white disabled:opacity-40"
            onClick={sendAll}
          >
            {sendingAny
              ? "발송 중…"
              : `완료된 초안 전체 발송 (${sendableCount})`}
          </button>
        )}
        {sentCount > 0 && (
          <div className="mt-2 text-center text-[12px] text-[#1D9E75]">
            {sentCount}건 발송 완료
          </div>
        )}
      </div>

      {pickerPet && (
        <MediaPickerModal
          pet={pickerPet}
          initial={petMedia[pickerPet.petSeq] ?? []}
          onConfirm={(picked) => {
            setPetMedia((prev) => ({ ...prev, [pickerPet.petSeq]: picked }));
            setPickerPet(null);
          }}
          onClose={() => setPickerPet(null)}
        />
      )}
    </div>
  );
}
